// ─────────────────────────────────────────────
//  useBarNameMenu — the state behind "click a NAME on the chart → the
//  quick-actions menu for that person".
//
//  ⚠️ ONE `BenchAssignFlow` MOUNT, TWO ENTRY POINTS. The Timeline already
//  mounts that menu for FREE NOW; the names on the bars open the SAME mount.
//  This repo's scar is explicit about it: "first a route I invented, then a
//  DIFFERENT placement sheet … find the component the working screen already
//  renders and mount THAT." A second mount would also be a second copy of eight
//  props to keep in step.
//
//  ⚠️ THE MENU NEEDS MORE THAN A NAME. `BenchAssignFlow` decides which options
//  to offer from the provider object it is handed:
//    · `givingBreaks` — without it "Stop Giving Breaks" cannot appear for
//      somebody who is currently giving them, so an AIC who set that label from
//      the bench could never clear it from the chart. It lives on the ROSTER
//      (`roster.givingBreaks[id]`), not on the staff profile and not on the
//      timeline case, so it has to be looked up here.
//    · `shiftCode` / `subRole` — carried by a Takeover, and printed in the
//      candidate list.
//  The bar knows an id and a name; everything else is resolved against today's
//  roster pool, which is the same list "Assign Supervising CRNA" picks from.
//
//  ⚠️ AND THE ROW TRAVELS WITH IT. `currentRow` is what tells the menu the
//  person is IN a room — that is what turns "New Room" into "Assign Supervising
//  CRNA" and stops it claiming a CRNA standing in OR 9 is on the free list.
//
//  ⚠️ THIS IS ALSO WHERE A NAME WITH NO ID BECOMES A PERSON. The owner's
//  report — *"why taylor prentice, kayla, briana, larson … have only one"* —
//  was five real CRNAs getting a one-item menu because their sheet cell never
//  resolved to a staff record at import, so the row stores `ahpId: null`. The
//  fix is to resolve the human, not to shrink their menu: `resolveBarIdentity`
//  looks the name up in the pool this page already holds, and when exactly one
//  real staff record answers, the id it returns is the id every write uses. See
//  `domain/secondProviderWrite` for the tie-breaks and for why an AMBIGUOUS
//  name is refused rather than guessed.
// ─────────────────────────────────────────────
import { useCallback, useRef, useState } from "react";
import { resolveBarIdentity } from "../../domain/secondProviderWrite";

// The people a chart name may be resolved AGAINST: today's roster first (its
// entries carry the shift code and are who is actually in the building), then
// the facility's whole provider master, which is where somebody the importer
// never enrolled still exists. Deduped downstream by id, so the overlap between
// the two is not mistaken for two people.
//
// ⚠️ THE ROSTER POOL CONTAINS PHANTOMS. `secondProviderMember` mints a derived
// entry with a synthetic `sp:<name>` id for precisely the unresolved people
// this lookup is about; `resolveProviderByName` drops them via
// `isResolvedProvider`, which is why the filtering must not be re-implemented
// here with a truthiness check.
const identityPool = (crnaPool, staffPool) => [
  ...(Array.isArray(crnaPool) ? crnaPool : []),
  ...(Array.isArray(staffPool) ? staffPool : []),
];

export default function useBarNameMenu({ crnaPool = [], staffPool = [], roster = null }) {
  // The provider object handed to `BenchAssignFlow`, or null (= closed).
  const [provider, setProvider] = useState(null);
  // The assignment row / timeline case the menu was opened from, or null when
  // it was opened from the bench (where the menu derives the row itself).
  const [currentRow, setCurrentRow] = useState(null);
  // Where focus goes when the menu closes. A ref OBJECT, held in a ref of our
  // own so re-renders do not hand `BenchAssignFlow` a new identity every time
  // and re-run its focus effect.
  const returnFocusRef = useRef(null);

  const close = useCallback(() => {
    setProvider(null);
    setCurrentRow(null);
    returnFocusRef.current = null;
  }, []);

  // FREE NOW: the bench entry already carries `givingBreaks` and the shift.
  const openForBenchProvider = useCallback((p) => {
    returnFocusRef.current = null;
    setCurrentRow(null);
    setProvider(p || null);
  }, []);

  // A NAME on a case bar.
  //
  // ⚠️ `id` MAY BE NULL, AND THAT IS THE POINT. It used to be a hard refusal —
  // a legacy "X orient Y" cell names two people and resolves neither, so those
  // names rendered as plain text and the owner reported them as "some crnas in
  // the timeline when you press dont work". They opened a one-item menu next,
  // because "Edit CRNA" writes the assignment row's SLOT and needs no id at
  // all — and the owner reported THAT as unequal treatment, correctly.
  //
  // ⚠️ SO THE NULL IS NOW ANSWERED, NOT PAPERED OVER. The name is looked up in
  // the staff pool; a single match gives a REAL id and the full menu follows on
  // its own, because every downstream test is `isResolvedProvider`. What must
  // never happen is substituting `key` for `id`: that would hand every id-keyed
  // write (Home, Transfer, Takeover) a synthetic `sp:<name>` that Firestore
  // will happily accept and nothing will ever read. `key` travels beside it,
  // for identity only.
  const openForName = useCallback(
    ({ id, key, name }, caseRow, triggerRef) => {
      const menuKey = key || id || null;
      if (!menuKey) return;
      const pool = identityPool(crnaPool, staffPool);
      const identity = resolveBarIdentity({ id, name }, pool);
      // The id ANY write may be keyed by — the row's own when it had one, the
      // single staff record that answers to the name when it did not, and null
      // when nobody or too many did.
      const resolvedId = identity.id || null;
      const member = identity.person || null;
      returnFocusRef.current = triggerRef?.current || null;
      setCurrentRow(caseRow || null);
      setProvider({
        id: resolvedId,
        // Distinct per PERSON, not per row: two unresolved names on one bar must
        // not both report themselves as the open one to `aria-expanded`.
        key: menuKey,
        name: member?.name || name || "",
        subRole: member?.subRole || "crna",
        shiftCode: member?.shiftCode ?? null,
        // ⚠️ FROM THE ROSTER, NOT FROM THE POOL ENTRY. `crnaPool` is the staff
        // master merged with the roster member; the break-giver label is a
        // separate map on the roster document and is not on either.
        givingBreaks: Boolean(resolvedId && roster?.givingBreaks?.[resolvedId]),
        // ⚠️ WHY THE MENU IS SHORT, WHEN IT IS SHORT — carried for COPY ONLY.
        // Whether a write may happen is still `isResolvedProvider(provider)`
        // downstream and nothing else; this is what lets the menu SAY that it
        // refused instead of silently offering less, which is the same failure
        // as a name that opened nothing, in a smaller form.
        identity: { status: identity.status, count: identity.matches.length },
      });
    },
    [crnaPool, staffPool, roster]
  );

  return {
    provider,
    currentRow,
    returnFocusRef,
    // Which trigger reports `aria-expanded="true"`. The KEY, not the id: an
    // unresolved name has no id, and two of them on one bar would otherwise
    // both match `null`.
    openFor: provider?.key || provider?.id || null,
    openForBenchProvider,
    openForName,
    close,
  };
}
